import { forkJoin as observableForkJoin } from 'rxjs';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { DataGroupDistributionObject, DataGroupPresentation } from './models';
import { DataGroupService } from './services/data-group.service';
import { PartnerSearch, PartnerSearchSettings, PartnerType, Partner } from '../partner/models';
import { PartnerService } from '../partner/services/partner.service';
import { GenericModalService, ModalSettingsMessage } from '../shared/generic-modal/generic-modal.service';
import { DocumentTypeEntity } from '../distribution/models';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { CurrentUserService } from './services/current-user.service';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { RestrictedSearchCalloutComponent } from '../shared/restricted-search/restricted-search.component';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, DxiButtonModule, DxoScrollingModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { FormsModule } from '@angular/forms';
import { TagSelectorComponent } from '../tags/tag-selector.component';
import { BizGridFiltervalues } from '../shared/grid/biz-grid-filtervalues';
import { GridService } from '../shared/grid/grid.service';

@Component({
    templateUrl: './data-group-crud.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, RestrictedSearchCalloutComponent, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxiColumnModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, FormsModule, TagSelectorComponent, DxoScrollingModule]
})
export class DataGroupCrudComponent implements OnInit {

    private readonly dataGroupService = inject(DataGroupService);
    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly currentUserService = inject(CurrentUserService);
    private readonly partnerService = inject(PartnerService);
    private readonly toastService = inject(ToastService);
    readonly bizGridFiltervalues = inject(BizGridFiltervalues);
    private readonly modalService = inject(GenericModalService);
    readonly gridService = inject(GridService);

    public searchSettings: PartnerSearchSettings = new PartnerSearchSettings();
    public datagroups: DataGroupPresentation[] = [];
    public selectedDataGroup: DataGroupPresentation;
    public editAllowed = false;
    public deleteAllowed = false;
    public showLogAllowed = false;
    public currentDataGroup: DataGroupPresentation = null;
    public dataGroupToDelete: DataGroupPresentation = null;
    public currentDataGroupOriginal: DataGroupPresentation = null;
    public partnerTypes: PartnerType[] = [];
    public allPartners: PartnerSearch[] = [];
    public availablePartners: PartnerSearch[] = [];
    public availablePartnersSelected: PartnerSearch[] = [];
    public selectedPartnersSelected: PartnerSearch[] = [];
    public selectedTags: any[] = [];
    public isSearchResultRestricted = false;
    public isSearchMaxResultReached = false;
    public isSearching = false;

    public allDistributionObjects: DocumentTypeEntity[] = [];
    public availableDistributionObjects: DocumentTypeEntity[] = [];
    public availableDistributionObjectsSelected: DocumentTypeEntity[] = [];
    public selectedDistributionObjectsSelected: DataGroupDistributionObject[] = [];

    public isLoading = false;

    @ViewChild('gridDataGroups', { static: true }) gridDataGroups: BizDataGridDirective;
    @ViewChild('gridAvailablePartners') gridAvailablePartners: BizDataGridDirective;
    @ViewChild('gridSelectedPartners') gridSelectedPartners: BizDataGridDirective;
    @ViewChild('gridAvailableDistributionObjects') gridAvailableDistributionObjects: BizDataGridDirective;
    @ViewChild('gridSelectedDistributionObjects') gridSelectedDistributionObjects: BizDataGridDirective;

    ngOnInit() {
        this.initPermissions();
        void this.refresh();
    }

    public onToolbarPreparing(e: any) {
        if (this.currentUserService.hasPermission('DataGroup_Action_Create')) {
            e.toolbarOptions.items.unshift({
                location: 'after',
                name: 'addNewCertificate',
                widget: 'dxButton',
                options: {
                    icon: 'add',
                    onClick: this.newDataGroup.bind(this)
                }
            });
        }
    }

    public initPermissions() {
        this.editAllowed = this.currentUserService.hasPermission('DataGroup_Action_Edit');
        this.deleteAllowed = this.currentUserService.hasPermission('DataGroup_Action_Delete');
        this.showLogAllowed = true;//this.currentUserService.hasPermission('DataGroup_Action_ShowLog');
    }

    public selectionChangedDataGroup(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            this.selectedDataGroup = e.selectedRowsData[0];
        }
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading Data Groups...');

            await Promise.all([
                this.dataGroupService.getDataGroupsForOverview().then(datagroups => { this.datagroups = datagroups.sort((g1, g2) => g1.Name.localeCompare(g2.Name)); }),
                this.partnerService.getPartnerTypes().then(result => { this.partnerTypes = result; }),
                this.dataGroupService.getDistributionObjects().then(result => { this.allDistributionObjects = result.sort((d1, d2) => d1.Name.localeCompare(d2.Name)); })
            ]);

            this.clearSearchSettings();
            this.isSearchResultRestricted = false;
            this.isSearchMaxResultReached = false;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    /*
        CanDeactivate
    */
    canDeactivate(): Promise<boolean> | boolean {
        return this.currentDataGroup ? this.askCanDeactivate() : true;
    }

    async askCanDeactivate(): Promise<boolean> {
        try {
            await this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?');
            return true;
        }
        catch {
            return false;
        }
    }

    async save(): Promise<void> {
        if (this.currentDataGroup.Partners.length === 0 && this.currentDataGroup.DistributionObjectConnections.length === 0) {
            if (this.currentDataGroup.DataGroupType === 'DocumentType') {
                await this.modalService.messageDirect('Error saving Data Group', 'At least one Document Type required to save Data Group');
            }
            if (this.currentDataGroup.DataGroupType === 'Partner') {
                await this.modalService.messageDirect('Error saving Data Group', 'At least one Partner required to save Data Group');
            }
            return;
        }

        if (this.currentDataGroup.Name === undefined) {
            await this.modalService.messageDirect('Error saving Data Group', 'Name is mandatory');
            return;
        }

        try {
            this.loadingPanelService.showLoadingPanelDirect('Saving data group...');

            if (this.currentDataGroup.Description === undefined) {
                this.currentDataGroup.Description = '';
            }

            if (this.currentDataGroup.DataGroupType === 'DocumentType') {
                await this.gridSelectedDistributionObjects.instance.saveEditData();
            }

            await this.dataGroupService.saveDataGroup(this.currentDataGroup);

            await this.cancel();
        } finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    public editSelected(datagroup: DataGroupPresentation) {
        this.currentDataGroupOriginal = datagroup;
        if(datagroup.DataGroupType === 'Partner'){

            const promises = [];
            const searchSettings = new PartnerSearchSettings();
            searchSettings.skipMaxSearchResult = false; // Make sure we show all available partners
            promises.push(this.partnerService.searchPartners(searchSettings).then(result => {
                this.isSearchResultRestricted = result.IsPartnerSearchResultRestricted;
                this.isSearchMaxResultReached = result.MaxSearchResultReached;
                this.allPartners = result.Partners.sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName));
                this.allPartners.forEach(x => {
                    x.PartnerTags = x.TagsNames.join(', ');
                });
            }));
            this.availablePartners = Object.assign([],this.allPartners);
            if (datagroup.Id > 0) {
                promises.push(this.dataGroupService.getDataGroup(datagroup.Id, true, true).then(group => {
                    this.currentDataGroup = group[0];
                    this.currentDataGroup.DataGroupType = datagroup.DataGroupType;
                }));
            }
            else {
                this.currentDataGroup = datagroup;
            }

            this.loadingPanelService.showLoadingPanelDirect('Loading datagroup...');
            observableForkJoin(promises).toPromise().then(() => {
                this.setSelectedPartners();
                this.loadingPanelService.hideLoadingPanel();
            });
        }
        if(datagroup.DataGroupType === 'DocumentType'){
            const promises = [];
            if (datagroup.Id > 0) {
                promises.push(this.dataGroupService.getDataGroup(datagroup.Id, true, true).then(group => {
                    this.currentDataGroup = group[0];
                    this.currentDataGroup.DataGroupType = datagroup.DataGroupType;
                }));
            }
            else {
                this.currentDataGroup = datagroup;
            }

            this.loadingPanelService.showLoadingPanelDirect('Loading datagroup...');
            observableForkJoin(promises).toPromise().then(() => {
                this.setSelectedDocumentType();
                this.loadingPanelService.hideLoadingPanel();
            });
        }
        if(datagroup.DataGroupType === 'Undecided'){
            this.currentDataGroup = datagroup;
        }
    }

    public newDataGroup() {
        const newDataGroup = new DataGroupPresentation();
        newDataGroup.DistributionObjectConnections = [];
        newDataGroup.Partners = [];
        newDataGroup.DataGroupType = 'Undecided';
        this.editSelected(newDataGroup);
    }

    public setGroupForPartners() {
        this.currentDataGroup.DataGroupType = 'Partner';
        this.editSelected(this.currentDataGroup);
    }
    public setGroupForDocumentTypes() {
        this.currentDataGroup.DataGroupType = 'DocumentType';
        this.editSelected(this.currentDataGroup);
    }

    public async cancel() {
        this.currentDataGroup = null;
        this.currentDataGroupOriginal = null;
        await this.gridDataGroups.instance.refresh(); // Force the grid to show changes...
        await this.refresh()
    }

    onRowDblClick($event: any): void {
        if(this.editAllowed) {
            const item = $event.data as DataGroupPresentation;
            this.editSelected(item);
        }
    }

    editItem = ($event: any) => {
        const item = $event.row.data as DataGroupPresentation;
        this.editSelected(item);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as DataGroupPresentation, item => this.removeDataGroup(item));
    };

    async removeDataGroup(datagroup: DataGroupPresentation): Promise<DataGroupPresentation> {
        if (datagroup.DataGroupType === 'Partner') {
            let partnersToValidate: Partner[] = [];
            const partnersFailedToRemove = [];
    
            this.dataGroupToDelete = (await this.dataGroupService.getDataGroup(datagroup.Id, true, false))[0] as DataGroupPresentation;
            partnersToValidate = await this.partnerService.getPartners(this.partnerIdsFromPartners(this.dataGroupToDelete.Partners));
    
            this.loadingPanelService.showLoadingPanelDirect('Deleting data group...');
    
            const validationResult = await this.dataGroupService.validatePartnerRemoval(this.partnerIdsFromPartners(this.dataGroupToDelete.Partners), this.dataGroupToDelete.Id);
            validationResult.forEach(result => {
                if (!result.RemovalAllowed) {
                    const partner = partnersToValidate.find(p => p.PartnersId === result.PartnerId);
                    partnersFailedToRemove.push(partner);
                }
            }, this);
    
            if (partnersFailedToRemove.length > 0) {
                let errorText = 'A partner must be associated with at least one data group. <br>The following partners could not be removed: <br><br><ul>';
                partnersFailedToRemove.forEach(p => {
                    errorText = errorText + '<li>' + p.PartnerName + '</li>';
                });
                this.loadingPanelService.hideLoadingPanel();
                void this.modalService.messageDirect('Error removing partner', errorText);
            } else {
                await this.dataGroupService.deleteDataGroup(datagroup.Id);
                this.loadingPanelService.hideLoadingPanel();
            }
        } else {
            this.loadingPanelService.showLoadingPanelDirect('Deleting data group...');
            await this.dataGroupService.deleteDataGroup(datagroup.Id);
            this.loadingPanelService.hideLoadingPanel();
        }
        return datagroup;    
    }

    //
    // PARTNERS
    //
    private partnerIdsFromPartners(partnerList: PartnerSearch[]): number[] {
        return partnerList.map(s=>s.PartnerId);
    }

    public addSelectedPartners() {
        this.availablePartnersSelected.forEach(p => {
            this.currentDataGroup.Partners.push(p);
        });
        this.setSelectedPartners();
        this.clearPartnerSelection();
    }

    private setSelectedPartners() {
        if (this.currentDataGroup) {
            this.availablePartners = Object.assign([],this.allPartners);

            const partnersCount = this.currentDataGroup.Partners.length;
            let partnersRemoved = 0;
            for( let i = 0; i < this.availablePartners.length; i++){
                for( let j = 0; j < this.currentDataGroup.Partners.length; j++){
                    if (this.availablePartners[i].PartnerId === this.currentDataGroup.Partners[j].PartnerId) {
                        this.availablePartners.splice(i, 1);
                        i--;
                        partnersRemoved +=1;
                        break;
                    }
                }
                if(partnersRemoved >= partnersCount){break;}
            }
        }
    }

    public removeSelectedPartners() {
        const partnersToValidate: PartnerSearch[] = [];
        const partnersFailedToRemove = [];

        this.loadingPanelService.showLoadingPanelDirect('Removing partner...');

        this.selectedPartnersSelected.forEach(p => {
                partnersToValidate.push(p);
        });

        const partnerIdsToValidate = partnersToValidate.map(p => p.PartnerId);

        this.dataGroupService.validatePartnerRemoval(partnerIdsToValidate, this.currentDataGroup.Id).then(validationResult => {
            validationResult.forEach(result => {
                if (result.RemovalAllowed) {
                    this.currentDataGroup.Partners.splice(this.currentDataGroup
                        .Partners.findIndex(partner => partner.PartnerId === result.PartnerId), 1); // If it's okay to remove the partner - remove it
                }
                else {
                    const partner = partnersToValidate.find(p => p.PartnerId === result.PartnerId);
                    partnersFailedToRemove.push(partner);
                }
            }, this);

            this.loadingPanelService.hideLoadingPanel();

            if (partnersFailedToRemove.length > 0) {
                let errorText = 'A partner must be associated with at least one data group. <br>The following partners could not be removed: <br><br><ul>';

                partnersFailedToRemove.forEach(p => {
                    errorText = errorText + '<li>' + p.PartnerName + '</li>';
                });

                const settings = new ModalSettingsMessage('Can not remove partner', errorText);
                this.modalService.message(settings).then((e) => { }).catch((e) => { });
            }

            partnersToValidate.forEach(p => {
                if(!this.allPartners.find(x=> x.PartnerId === p.PartnerId)){
                    this.allPartners.push(p);
                }
            });
            this.setSelectedPartners();
        });
        this.clearPartnerSelection();
    }

    search(): void {

        const promises = [];
        this.loadingPanelService.showLoadingPanelDirect('Searching...');
        this.isSearching = true;
        this.searchSettings.searchTags = []; // Reset tags before adding new ones, if any are selected.
        this.searchSettings.skipMaxSearchResult = false; // Make sure we show all available partners
        this.selectedTags.forEach(s => {
            this.searchSettings.searchTags.push(s.value);
        });

        promises.push(this.partnerService.searchPartners(this.searchSettings).then(partnerResult => {
            this.allPartners = partnerResult.Partners.sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName));
            this.isSearchResultRestricted = partnerResult.IsPartnerSearchResultRestricted;
            this.isSearchMaxResultReached = partnerResult.MaxSearchResultReached;

            this.allPartners.forEach(x => {
                x.PartnerTags = x.TagsNames.join(', ');
            });

            this.availablePartners = Object.assign([],this.allPartners);

            this.isSearching = false;
        }).catch(() => {
            this.loadingPanelService.hideLoadingPanel();
            this.isSearching = false;
        }));

        this.loadingPanelService.showLoadingPanelDirect('Loading datagroup...');
            observableForkJoin(promises).toPromise().then(() => {
                this.setSelectedPartners();
                this.loadingPanelService.hideLoadingPanel();
            });
    }

    clear(): void {
        this.clearSearchSettings();
        const promises = [];
        const searchSettings = new PartnerSearchSettings();
        searchSettings.skipMaxSearchResult = false; // Make sure we show all available partners
        promises.push(this.partnerService.searchPartners(searchSettings).then(result => {
            this.allPartners = result.Partners.sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName));
            this.isSearchResultRestricted = result.IsPartnerSearchResultRestricted;
            this.isSearchMaxResultReached = result.MaxSearchResultReached;
            this.allPartners.forEach(x => {
                x.PartnerTags = x.TagsNames.join(', ');
            });
        }));
        this.availablePartners = Object.assign([],this.allPartners);

        this.loadingPanelService.showLoadingPanelDirect('Loading datagroup...');
        observableForkJoin(promises).toPromise().then(() => {
            this.setSelectedPartners();
            this.loadingPanelService.hideLoadingPanel();
        });
    }

    private clearSearchSettings(): void {
        this.searchSettings = new PartnerSearchSettings();
    }

    public selectionChangedAvailablePartners(e: any) {
        this.availablePartnersSelected = e.selectedRowsData;
    }

    public selectionChangedSelectedPartners(e: any) {
        this.selectedPartnersSelected = e.selectedRowsData;
    }

    public clearPartnerSelection() {
        this.gridAvailablePartners.instance.clearSelection();
        this.gridSelectedPartners.instance.clearSelection();
    }

    selectedTagsChange(e: any): void {
        this.selectedTags = e;
    }

    //
    // DISTRIBUTION OBJECTS
    //
    private setSelectedDocumentType() {
        if (this.currentDataGroup) {
            this.availableDistributionObjects = Object.assign([],this.allDistributionObjects);

            for(let i = 0; i < this.availableDistributionObjects.length; i++) {
                for(let j = 0; j< this.currentDataGroup.DistributionObjectConnections.length; j++){
                    if(this.currentDataGroup.DistributionObjectConnections[j].DistributionObject.Id === this.availableDistributionObjects[i].Id) {
                        this.availableDistributionObjects.splice(i,1);
                        i--;
                        break;
                    }
                }
            }
        }
    }

    public addSelectedDistributionObjects() {
        this.availableDistributionObjectsSelected.forEach(d => {
            if (!this.currentDataGroup.DistributionObjectConnections.some(sd => sd.DistributionObject.Id === d.Id)) {

                // Create a new DistributionObjectConnection because that's whats needed by the data-group
                const dataGroupDistributionObject = new DataGroupDistributionObject();
                dataGroupDistributionObject.DistributionObject = d;
                dataGroupDistributionObject.AccessTrackingDocuments = false;
                this.currentDataGroup.DistributionObjectConnections.push(dataGroupDistributionObject);
            }
        });
        this.clearDistributionObjectSelection();
        this.setSelectedDocumentType();
    }

    public removeSelectedDistributionObjects() {
        this.selectedDistributionObjectsSelected.forEach(d => {
            this.currentDataGroup.DistributionObjectConnections.splice(this.currentDataGroup.DistributionObjectConnections.indexOf(d), 1);
        });
        this.clearDistributionObjectSelection();
        this.setSelectedDocumentType();
    }

    public selectionChangedAvailableDistributionObjects(e: any) {
        this.availableDistributionObjectsSelected = e.selectedRowsData;
    }

    public selectionChangedSelectedDistributionObjects(e: any) {
        this.selectedDistributionObjectsSelected = e.selectedRowsData;
    }

    public clearDistributionObjectSelection() {
        this.gridAvailableDistributionObjects.instance.clearSelection();
        this.gridSelectedDistributionObjects.instance.clearSelection();
    }
}
